import React from 'react';
import { Card, Button } from 'flowbite-react';
import { FiCalendar } from 'react-icons/fi';
import ClearLocalCacheButton from '../../components/common/ClearLocalCacheButton';
import AvailabilityPolicyForm from '../../components/forms/AvailabilityPolicyForm';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import AvailabilityList from '../../components/availability/AvailabilityList';
import * as wasm from 'sched';

const SettingsPage: React.FC = () => {
  const { workspaceId } = useWorkspace();
  const { applyCommand, database } = useDatabase();

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Workspace Settings</h1>

      {/* Default Availability */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="w-5 h-5 text-cm-accent-blue" />
          <h2 className="text-xl font-mono font-semibold text-cm-text-primary">Default Availability</h2>
        </div>
        <p className="text-xs text-cm-text-muted mb-3">
          Workspace-wide defaults. Lowest precedence — overridden by team and individual policies.
        </p>
        {(() => {
          const items = database?.default_availability() || [];

          const mapped = items
            .filter((a: wasm.PersonAvailability) => !!a.id)
            .map((a: wasm.PersonAvailability) => ({ id: a.id as string, policy: a.policy, after: a.after, before: a.before }));

          const onDelete = async (id: string) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdateDefaultAvailability',
              actions: [{ Delete: { id } }],
            };
            await applyCommand(command);
          };

          const onReorder = async ({ id, before }: { id: string; before: string | null }) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdateDefaultAvailability',
              actions: [{ Reorder: { id, before } }],
            };
            await applyCommand(command);
          };

          return (
            <div className="mb-4">
              <AvailabilityList items={mapped} onDelete={onDelete} onReorder={onReorder} emptyText="No default availability policies yet." />
            </div>
          );
        })()}

        <AvailabilityPolicyForm
          onSubmit={async (availability) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdateDefaultAvailability',
              actions: [{ Upsert: { availability } }],
            };
            await applyCommand(command);
          }}
        />
      </Card>

      {/* Data Management Section */}
      <Card>
        <h2 className="text-xl font-mono font-semibold text-cm-text-primary mb-4">Data Management</h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-2">Local Cache</h3>
            <p className="text-xs text-cm-text-muted mb-3">
              Clear the local cache for this workspace. This will remove all locally stored data and reload from the server.
            </p>
            <div className="flex gap-3">
              <ClearLocalCacheButton workspaceId={workspaceId} />
              <Button
                color="gray"
                size="sm"
                onClick={async () => {
                  if (!workspaceId) return;
                  try {
                    const raw = await wasm.DataManager.load_database_from_storage_but_do_not_deserialize_yet(workspaceId);
                    if (!raw) {
                      alert('No database found in local storage.');
                      return;
                    }
                    const json = JSON.stringify(raw, null, 2);
                    const blob = new Blob([json], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `database-${workspaceId}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error('Failed to export database:', e);
                    alert(`Export failed: ${e}`);
                  }
                }}
              >
                Download Database
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SettingsPage;
